'use client'

import CloseIcon from '@trustoo/components/ui/icon/CloseIcon'
import InfoIcon from '@trustoo/components/ui/icon/InfoIcon'
import clsx from 'clsx'
import { useCallback, useEffect, useRef } from 'react'
import type { ReactElement, ReactNode } from 'react'
import { useTransitionState } from 'react-transition-state'
import type { TransitionState, TransitionStatus } from 'react-transition-state'
import { twMerge } from 'tailwind-merge'

export interface FixedWarningPopupProps {
    show: boolean
    fixed?: boolean
    children: ReactNode
    red?: boolean
    orange?: boolean
    green?: boolean
    fromTop?: boolean
    className?: string
    timeout?: number | null
    /**
     * Leading icon shown before the message.
     * Pass any icon element (e.g. `<ErrorOutlineIcon />`) or `false` to hide it. Defaults to `<InfoIcon />`.
     */
    icon?: ReactElement | false
    /**
     * Changing this value restarts the auto-dismiss timer without remounting. Use it to
     * "refresh" a persistent popup (e.g. a toast re-shown under a stable React key) so its
     * countdown starts over instead of continuing to run from the original mount.
     */
    restartKey?: string | number
    closeBtn?: boolean
    /**
     * ARIA role for the popup element. Use `alert` for something the user must be told at once (a
     * failed write), `status` for an acknowledgement. Both are announced when the element is
     * inserted, which a plain `aria-live` region is not unless it was already in the DOM.
     */
    role?: 'alert' | 'status'
    onExited?: () => void
}

const getTransitionClasses = (status: TransitionStatus, fromTop?: boolean): string => {
    switch (status) {
        case 'preEnter':
            return fromTop ? 'opacity-0 -translate-y-[10%]' : 'opacity-0 translate-y-full'
        case 'entering':
            return 'opacity-100 translate-y-0 transition-all duration-300 ease'
        case 'preExit':
            return 'opacity-100'
        case 'exiting':
        case 'exited':
            return 'opacity-0 transition-all duration-500 ease'
        default:
            return ''
    }
}

const getBackgroundClass = (red?: boolean, orange?: boolean, green?: boolean): string => {
    if (red) return 'bg-[#e74c3c]'
    if (orange) return 'bg-[#ff7d3c]'
    if (green) return 'bg-[#0ea66b]'
    return 'bg-[#0b1a37]'
}

const FixedWarningPopup = ({
    show,
    fixed,
    children,
    red,
    orange,
    green,
    fromTop,
    className = '',
    timeout = 5000,
    icon,
    restartKey,
    closeBtn = false,
    role,
    onExited = () => {},
}: FixedWarningPopupProps) => {
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
    const onExitedRef = useRef(onExited)
    const shouldCallOnExitedRef = useRef(false)

    useEffect(() => {
        onExitedRef.current = onExited
    }, [onExited])

    const onStateChange = useCallback(({ current }: { current: TransitionState }) => {
        if (
            (current.status === 'exited' || current.status === 'unmounted') &&
            shouldCallOnExitedRef.current
        ) {
            shouldCallOnExitedRef.current = false
            onExitedRef.current()
        }
    }, [])

    const [transitionState, toggle] = useTransitionState({
        timeout: {
            enter: 300,
            exit: 500,
        },
        mountOnEnter: true,
        unmountOnExit: true,
        preEnter: true,
        preExit: true,
        onStateChange,
    })

    const onDismiss = useCallback(() => {
        clearTimeout(timeoutRef.current)
        toggle(false)
    }, [toggle])

    useEffect(() => {
        clearTimeout(timeoutRef.current)

        if (show) {
            shouldCallOnExitedRef.current = true
        }

        toggle(show)

        if (!show) return

        if (timeout) {
            timeoutRef.current = setTimeout(onDismiss, timeout)
        }

        return () => clearTimeout(timeoutRef.current)
    }, [onDismiss, show, timeout, toggle, restartKey])

    return (
        <div
            role={role}
            className={twMerge(
                'bottom-0 left-0 right-0 mx-auto max-w-[450px] overflow-hidden',
                fixed ? 'fixed z-[999]' : 'absolute z-[9999] max-md:fixed',
                className,
            )}>
            {transitionState.isMounted && (
                <div
                    onClick={onDismiss}
                    className={clsx(
                        'grid grid-cols-[1fr_auto] text-white p-2 rounded-t-[5px] cursor-pointer',
                        '[&_a]:text-white [&_a]:underline',
                        getBackgroundClass(red, orange, green),
                        getTransitionClasses(transitionState.status, fromTop),
                    )}>
                    <div className="px-[6px]">
                        {icon === false ? (
                            children
                        ) : (
                            <div className="flex items-center gap-[5px]">
                                <span className="flex-shrink-0">{icon || <InfoIcon />}</span>
                                <span>{children}</span>
                            </div>
                        )}
                    </div>
                    {closeBtn && <CloseIcon className="opacity-70" size={10} />}
                </div>
            )}
        </div>
    )
}

export default FixedWarningPopup
